import { makeAutoObservable, runInAction } from "mobx";
import { Ref, useRef } from "react";
import { NavigateFunction } from "react-router-dom";
import Selecto, { ElementType } from "selecto";
import { v4 as uuidv4 } from "uuid";
import { SecondaryTabTypeEnum } from "../../../DesignEditor/views/DesignEditor/components/Panels/panelItems/index.ts";
import { ANALYTICS_EVENTS } from "../../../analytics-store.tsx";
import { AspectRatio } from "../../../components/common/DropDowns/StaticDropdown.tsx";
import { FullScreenImageType } from "../../../components/common/FullScreenViewer/Image/FullScreenImage.tsx";
import { BriaImageProps, ImageErrorType } from "../../../components/common/Galleries/BriaImage.tsx";
import useSelectable from "../../../components/common/Selectable/useSelectable.tsx";
import { APPS, LABS } from "../../../constants/AppsConstants.ts";
import {
	DEFAULT_BODY_INTENSITY,
	DEFAULT_TIRES_INTENSITY,
	DEFAULT_WINDOWS_INTENSITY,
} from "../../../constants/AutomotiveConstants.ts";
import { LocalStorageConstants } from "../../../constants/LocalStorageConstants.ts";
import RouterConstants from "../../../constants/RouterConstants.ts";
import { USER_SETTINGS } from "../../../constants/UserConstants.ts";
import useErrorToast from "../../../hooks/useErrorToast.tsx";
import useParentPath from "../../../hooks/useParentPath.tsx";
import { IRootStore } from "../../../mobx/root-store.tsx";
import {
	ImageEditingViewMode,
	ImageToImageConfigType,
	PlaygroundImage,
	PlaygroundResult,
} from "../../../models/image-to-image.ts";
import {
	AutomotiveImageReflection,
	AutomotiveImageSegmentation,
	AutomotiveImageSegments,
	AutomotiveReflectionLayer,
	PlaygroundVideoResult,
	TextResult,
} from "../../../models/playground.ts";
import { TextToImageConfigType } from "../../../models/text-to-image.ts";
import { VideoEditingConfigType } from "../../../models/video-editing.ts";
import AutomotiveService from "../../../services/AutomotiveService.ts";
import FeedbackService from "../../../services/FeedbackService.ts";
import ImagesService from "../../../services/ImagesService.ts";
import { isFoxApps } from "../../../utils/index.ts";
import { showErrorToast } from "../../../utils/toast.tsx";
import useImageUtils from "../../../utils/useImageUtils.tsx";
import {
	IPlaygroundProjectsStore,
	PlaygroundProjectsStore,
} from "../../Automotive/Projects/store/playground-projects-store.tsx";
import {
	IPlaygroundSessionsStore,
	PlaygroundSessionsStore,
} from "../../Automotive/Sessions/store/playground-sessios-store.tsx";
import { FiboEditConfigType } from "../../FiboEdit/fibo-edit-store.tsx";
import iframeStore from "../../IframeNew/iframe-store.tsx";
import { AutomotiveStore, IAutomotiveStore } from "../Automotive/automotive-store.tsx";
import { Feedback, FeedbackTypeEnum } from "../Images/Overlay/PlaygroundImageOverlay.tsx";
import { PlaygroundVideoProps } from "../VideoGeneration/Video/PlaygroundVideo.tsx";
import { IPlaygroundResultsStore, PlaygroundResultsStore } from "./playground-results-store.tsx";

// Leaves our UI in two places: the export dialog's editable name field, and the fileName an
// iframe host stores the asset under. Keep it a plausible filename, not an internal label.
const DEFAULT_EXPORT_FILE_NAME = "output_image";

// Types for lab result groups (shared across all labs)
export type LabConfigType = TextToImageConfigType | FiboEditConfigType | VideoEditingConfigType;

export interface ILabResultGroup {
	id: string;
	loading: boolean;
	selectedImage: PlaygroundImage | PlaygroundVideoProps;
	config: LabConfigType;
}

export interface IFiboResultGroup extends ILabResultGroup {
	structured_prompt?: string;
	original_structured_prompt?: string;
	structured_prompt_diff?: string;
	delta_prompt?: string;
	seed?: number;
	is_refine: boolean;
	is_structured_prompt_loading: boolean;
	config: TextToImageConfigType | FiboEditConfigType;
}

export interface LastFeedback {
	feedbackType: FeedbackTypeEnum;
	response: any;
	data: any;
}

export interface IPlaygroundStore {
	selectedConfig: APPS | LABS;
	playgroundResults: PlaygroundResult[];
	textResults: TextResult[];
	playgroundVideoResults: PlaygroundVideoResult[];
	selectoRef?: Ref<Selecto>;
	loadingExport: boolean;
	loadingSaveToMyImages: boolean;
	loadingSaveToMyImagesAndInsertImageToAd: boolean;
	isText2ImagePopupOpened: boolean;
	showSuccessToastSavedImage: boolean;
	playGroundLoadedImages: PlaygroundImage[];
	textToGenerate: string;
	aspectRatio: AspectRatio;
	setTextToGenerate: (textToGenerate: string) => void;
	setSelectedAspectRatio: (aspectRatio: AspectRatio) => void;
	openModal: () => void;
	closeModal: () => void;
	saveMyImages: (action: SaveImagesLoadingState) => void;
	deleteResult: (id: string) => void;
	getSelecto: () => Selecto | null;
	clearResults: () => void;
	ResetResults: () => void;
	exportImages: (
		images: PlaygroundImage[],
		zipName?: string,
		resolution?: number,
		action?: "download" | "save",
		additionalFiles?: File[],
		fileType?: string,
		fileName?: string,
	) => Promise<void>;
	getAvailableImages: (includeLoading?: boolean) => PlaygroundImage[];
	getSelectedImages: () => PlaygroundImage[];
	getSelectedImagesForExport: () => PlaygroundImage[];
	openSelected: () => void;
	handleSelectImagesElements: (imagesElements?: ElementType[], isPopupView?: boolean) => void;
	selectImages: (images: PlaygroundImage[]) => void;
	getFileName: (images: PlaygroundImage) => string;
	onSuccessResult: (image: PlaygroundImage) => Promise<void>;
	onErrorResult: (image: PlaygroundImage, errorType: ImageErrorType) => Promise<void>;
	loadingFeedback: boolean;
	feedbackHistory: Feedback[];
	addOrUpdateFeedback: (newFeedback: Feedback) => Promise<void>;
	clearSelectedImages: () => void;
	handleSelectImage: (image: PlaygroundImage) => void;
	uploadedImagesURLs: string[];
	handleAppChange: (app: APPS) => void;
	onAiEditorButtonClick: (
		image: PlaygroundImage,
		navigate: NavigateFunction,
		forceOldEditor?: boolean,
	) => Promise<void>;
	imageEditingViewMode: ImageEditingViewMode;
	automotiveViewMode: ImageEditingViewMode;
	enablePlaygroundBrushMode: boolean;
	togglePlaygroundViewMode: () => void;
	togglePlaygroundBrushMode: (status: boolean) => void;
	handleSelectSingleImage: (image: PlaygroundImage | null) => void;
	singleSelectedImage: PlaygroundImage | null;
	selectedResult: PlaygroundResult | null;
	readonly selectedSingleImage: PlaygroundImage | null;
	openUploadModal: () => void;
	closeUploadModal: () => void;
	isUploadModalOpened: boolean | null;
	addTextResults: (textResult: TextResult) => void;
	clearTextResults: () => void;
	removeNotLoadingImages: () => void;
	clearVideoResults: () => void;
	onFullScreenViewerDownload: (
		images: (BriaImageProps & PlaygroundImage)[],
		fullscreenImages: FullScreenImageType[],
	) => void;
	projectsStore: IPlaygroundProjectsStore;
	sessionsStore: IPlaygroundSessionsStore;
	automotiveStore: IAutomotiveStore;
	imagesReflections: AutomotiveImageReflection[];
	imagesEffects: AutomotiveImageReflection[];
	imagesHarmonizations: AutomotiveImageReflection[];
	imagesSegments: AutomotiveImageSegments[];
	generateReflectionLayers: () => Promise<void>;
	generateTiresLayers: () => Promise<void>;
	getAutomotiveImageReflections: (imageUrl: string) => AutomotiveImageReflection | null;
	getAutomotiveImageEffects: (imageUrl: string) => AutomotiveImageReflection | null;
	getAutomotiveImageHarmonizations: (imageUrl: string) => AutomotiveImageReflection | null;
	getAutomotiveImageSegments: (imageUrl: string) => AutomotiveImageSegments | null;
	addLoadedImageToStore: (image: PlaygroundImage) => void;
	clearPlayGroundLoadedImagesArray: () => void;
	isLoadingSegments: boolean;
	isLoadingReflections: boolean;
	enableAutomotiveReflectionMode: boolean;
	toggleAutomotiveReflectionMode: (status: boolean) => void;
	selectedReflectionLayer: AutomotiveReflectionLayer | null;
	handleSelectReflectionLayer: (layer: AutomotiveReflectionLayer | null) => void;
	selectedSegmentation: AutomotiveImageSegmentation | null;
	handleSelectSegmentation: (layer: AutomotiveImageSegmentation | null) => void;
	updateReflectionLayerIntensity: (imageUrl: string, layerName: string, intensity: number) => void;
	updateEffectLayerIntensity: (imageUrl: string, layerName: string, intensity: number) => void;
	updateHarmonizationLayerIntensity: (imageUrl: string, layerName: string, intensity: number) => void;
	showHideReflectionLayer: (imageUrl: string, layerName: string, status: boolean) => void;
	editReflectionLayer: (mask: string, newReflection: File) => Promise<void>;
	editTireLayer: (newReflection: File) => Promise<void>;
	isEditingReflection: boolean;
	disableSelecto: boolean;
	selectedFiboResultId: string | null;
	setSelectedFiboResultId: (id: string | null) => void;
	lastResultImage: { element: HTMLElement; imageId: string } | null;
	lastFeedback: LastFeedback | null;

	/** An easy-access variable to get the lab/fibo result groups */
	get labResultGroups(): ILabResultGroup[];
	isFiboResultGroup: (group?: ILabResultGroup) => group is IFiboResultGroup;
}

export default class PlaygroundStore implements IPlaygroundStore {
	private feedbackService = new FeedbackService();
	rootStore: IRootStore;
	projectsStore: IPlaygroundProjectsStore;
	sessionsStore: IPlaygroundSessionsStore;
	resultsStore: IPlaygroundResultsStore;
	automotiveStore: IAutomotiveStore;
	selectedConfig: APPS | LABS = APPS.TEXT_TO_IMAGE;
	playgroundResults: PlaygroundResult[] = [];
	textResults: TextResult[] = [];
	playgroundVideoResults: PlaygroundVideoResult[] = [];
	selectedImages: PlaygroundImage[] = [];
	selectoRef = useRef<Selecto>(null);
	loadingExport: boolean = false;
	loadingSaveToMyImages: boolean = false;
	loadingSaveToMyImagesAndInsertImageToAd: boolean = false;
	private imageUtils = useImageUtils();
	private parentPath = useParentPath(window.location);
	errorToast = useErrorToast();
	isText2ImagePopupOpened: boolean = false;
	showSuccessToastSavedImage: boolean = false;
	playGroundLoadedImages: PlaygroundImage[] = [];
	loadingFeedback: boolean = false;
	feedbackHistory: Feedback[] = [];
	private automotiveQueryService: AutomotiveService = new AutomotiveService();
	textToGenerate: string = "";
	aspectRatio: AspectRatio = "4:3";
	uploadedImagesURLs: string[] = [];
	imageEditingViewMode: ImageEditingViewMode = ImageEditingViewMode.SINGLE;
	automotiveViewMode: ImageEditingViewMode = ImageEditingViewMode.SINGLE;
	enablePlaygroundBrushMode: boolean = false;
	singleSelectedImage: PlaygroundImage | null = null;
	selectedResult: PlaygroundResult | null = null;
	isUploadModalOpened: boolean | null = null;
	imagesReflections: AutomotiveImageReflection[] = [];
	imagesEffects: AutomotiveImageReflection[] = [];
	imagesHarmonizations: AutomotiveImageReflection[] = [];
	imagesSegments: AutomotiveImageSegments[] = [];
	isLoadingReflections: boolean = false;
	isLoadingSegments: boolean = false;
	enableAutomotiveReflectionMode: boolean = false;
	selectedReflectionLayer: AutomotiveReflectionLayer | null = null;
	selectedSegmentation: AutomotiveImageSegmentation | null = null;
	isEditingReflection: boolean = false;
	disableSelecto: boolean = false;
	selectedFiboResultId: string | null = "";
	lastResultImage: { element: HTMLElement; imageId: string } | null = null;
	lastFeedback: LastFeedback | null = null;

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
		this.projectsStore = new PlaygroundProjectsStore(this);
		this.sessionsStore = new PlaygroundSessionsStore(this);
		this.resultsStore = new PlaygroundResultsStore(this);
		this.automotiveStore = new AutomotiveStore(this);
	}

	onFullScreenViewerDownload = (
		images: (BriaImageProps & PlaygroundImage)[],
		fullscreenImages: FullScreenImageType[],
	) => {
		const foundImages: PlaygroundImage[] = [];
		fullscreenImages.forEach((fullscreenImage) => {
			const foundImage = images.find((img) => img.url === fullscreenImage.src);
			if (foundImage) {
				foundImages.push(foundImage);
			}
		});
		this.rootStore.uiStore.showExportImagePopup({ images: foundImages });
	};

	togglePlaygroundViewMode = (): void => {
		runInAction(() => {
			if (
				iframeStore.isIframe() &&
				((this.selectedConfig !== APPS.AUTOMOTIVE &&
					!iframeStore.iframe.config.image_editing_config?.enable_bulk) ||
					(this.selectedConfig == APPS.AUTOMOTIVE &&
						!iframeStore.iframe.config.automotive_config?.enable_bulk))
			) {
				this.imageEditingViewMode = ImageEditingViewMode.SINGLE;
			} else {
				this.imageEditingViewMode =
					this.imageEditingViewMode === ImageEditingViewMode.SINGLE
						? ImageEditingViewMode.GALLERY
						: ImageEditingViewMode.SINGLE;
				this.enablePlaygroundBrushMode = false;
			}
		});
	};

	togglePlaygroundBrushMode = (status: boolean): void => {
		runInAction(() => {
			this.enablePlaygroundBrushMode = status;
		});
	};

	toggleAutomotiveReflectionMode = (status: boolean): void => {
		runInAction(() => {
			this.enableAutomotiveReflectionMode = status;
		});
	};

	setTextToGenerate = (textToGenerate: string): void => {
		this.textToGenerate = textToGenerate;
	};

	setSelectedAspectRatio = (aspectRatio: AspectRatio): void => {
		this.aspectRatio = aspectRatio;
	};

	handleAppChange = (app: APPS) => {
		runInAction(() => {
			this.selectedConfig = app;
		});
	};

	deleteResult = async (id: string) => {
		if (
			this.selectedConfig === APPS.AUTOMOTIVE &&
			(!iframeStore.isIframe() || iframeStore.iframe.config.automotive_config?.enable_project_management)
		) {
			await this.resultsStore.deleteResult(id);
		}
		this.playgroundResults = this.playgroundResults.filter((result) => result.id !== id);
	};

	clearVideoResults = (): void => {
		this.playgroundVideoResults = [];
	};

	onSuccessResult = async (image: PlaygroundImage): Promise<void> => {
		const foundImage = this.rootStore.playgroundStore.playgroundResults
			.flatMap((result) => result.images)
			.find((searchSameImg) => searchSameImg.id === image.id);
		if (foundImage) {
			foundImage.loading = false;
		}
	};

	addTextResults = (textResult: TextResult) => {
		runInAction(() => {
			this.textResults.push(textResult);
		});
	};

	clearTextResults = () => {
		runInAction(() => {
			this.textResults = [];
		});
	};

	onAiEditorButtonClick = async (
		image: PlaygroundImage,
		navigate: NavigateFunction,
		_forceOldEditor: boolean = false,
	): Promise<void> => {
		if (!isFoxApps()) {
			this.selectImages([image]);
			this.imageEditingViewMode = ImageEditingViewMode.SINGLE;
			this.selectedConfig = APPS.IMAGE_TO_IMAGE;
			navigate(
				this.parentPath.includes(RouterConstants.APPS.fullPath)
					? RouterConstants.IMAGE_TO_IMAGE.fullPath
					: RouterConstants.IMAGE_EDITING.IMAGE.DEMO.fullPath,
			);
		} else {
			this.rootStore.aiEditorStore.setProperty("selectedImageUrl", image.url);
			this.rootStore.aiEditorStore.setProperty("aiEditorPopup", true);
			this.handleSelectSingleImage(image);
			if (image.type === APPS.IMAGE_TO_IMAGE) {
				this.rootStore.objectsStore.setProperty("refine", {
					...this.rootStore.objectsStore.refine,
					tailored_style: (image.config as ImageToImageConfigType).style?.tailored_style,
					sub_style: (image.config as ImageToImageConfigType).style?.sub_style,
					originalImage: (image.config as ImageToImageConfigType).original_image.url,
				});
			}
		}
	};

	onErrorResult = async (image: PlaygroundImage, errorType: ImageErrorType): Promise<void> => {
		this.rootStore.analyticsStore.logImageToImageEvent(ANALYTICS_EVENTS.IMAGE_ERROR_ITI, image);
		const foundImage = this.rootStore.playgroundStore.playgroundResults
			.flatMap((result) => result.images)
			.find((searchSameImg) => searchSameImg.id === image.id);
		if (foundImage) {
			foundImage.loading = false;
			foundImage.error = { type: errorType };
		}
	};

	exportImages = async (
		images: PlaygroundImage[],
		zipName: string = "bria_gen",
		resolution: number = 1,
		action: "download" | "save" = "download",
		additionalFiles?: File[],
		fileType?: string,
		fileName?: string,
	) => {
		try {
			this.loadingExport = true;

			if ([...images, ...(additionalFiles || [])].length === 1) {
				await this.imageUtils.exportImage(
					images[0].url,
					this.getFileName(images[0]),
					resolution,
					action,
					fileType,
				);
			} else {
				const scaledImages = [];
				for (const image of images) {
					const scaledUrl =
						resolution === 1
							? image.url
							: await new ImagesService().increaseResolution(image.url, resolution);

					scaledImages.push({ fileName: fileName || this.getFileName(image), imageUrl: scaledUrl });
				}

				await this.imageUtils.exportZipImages(scaledImages, zipName, action, additionalFiles, fileType);
			}

			// Reset loading state
			runInAction(() => {
				this.loadingExport = false;
			});
		} catch (e: any) {
			this.loadingExport = false;
			return Promise.reject(`Error downloading images zip: ${e.message || e.toString()}`);
		}
	};

	clearResults = () => {
		runInAction(() => {
			this.playgroundResults = [];
			this.selectedImages = [];
			this.textResults = [];
			this.playgroundVideoResults = [];
			this.clearPlayGroundLoadedImagesArray();
		});
	};

	// for IFrame:Reset all the results except the starting image
	ResetResults = () => {
		runInAction(() => {
			this.playgroundResults = this.playgroundResults.slice(0, 1);
			this.selectedImages = this.selectedImages.slice(0, 1);
			this.textResults = [];
			this.playgroundVideoResults = [];
			this.clearPlayGroundLoadedImagesArray();
		});
	};

	getSelecto = () => this.selectoRef.current;

	handleSelectImagesElements = (imagesElements?: ElementType[], isPopupView: boolean = false) => {
		if (!isPopupView && this.rootStore.imagesStore.secondaryTabType !== SecondaryTabTypeEnum.SMART_IMAGE) {
			const selectedImagesIds: string[] | undefined = imagesElements?.map((el) =>
				JSON.parse(el.ariaValueText ?? "{}"),
			);
			this.playgroundResults.forEach((result) =>
				result.images?.forEach((image) => {
					image.selected = selectedImagesIds?.some((id) => id === image.id);
				}),
			);
		} else {
			// Get the ID of the last selected image element, if any
			if (imagesElements && imagesElements.length > 0 && imagesElements[0] != undefined) {
				const lastSelectedImageId = imagesElements?.length
					? JSON.parse(imagesElements[imagesElements.length - 1]?.ariaValueText ?? "")
					: null;

				// Update the selection state for each last Image Id
				this.playgroundResults.forEach((result) => {
					result.images?.forEach((image) => {
						// Set selected to true only if it matches the last selected image ID
						image.selected = image.id === lastSelectedImageId;
					});
				});
			} else {
				imagesElements = [];
				const selectedImagesIds: string[] | undefined = imagesElements.map((element) =>
					JSON.parse(element.ariaValueText ?? "{}"),
				);
				this.playgroundResults.forEach((result) =>
					result.images?.forEach((image) => {
						image.selected = selectedImagesIds?.some((id) => id === image.id);
					}),
				);
			}
		}
	};

	selectImages = (images: PlaygroundImage[]) => {
		// Timeout to assure that playgroundResults HTML Elements are rendered before
		setTimeout(() => {
			const elementsToSelect: ElementType[] | undefined = this.getSelecto()
				?.getSelectableElements()
				?.filter((el) => images.some((image) => image.id === JSON.parse(el.ariaValueText ?? "{}")));
			this.handleSelectImagesElements(elementsToSelect);
			useSelectable(this.getSelecto()).selectElements(elementsToSelect);
		}, 1000);
	};

	getFileName = (image: PlaygroundImage): string => {
		if (image.type === "textToImage") {
			return (image.config as TextToImageConfigType)?.prompt?.substring(0, 80) ?? "";
		} else if (image.type === APPS.TG_IMAGE_GENERATION) {
			return `tailored_generation_${(image.config as TextToImageConfigType).tailored_model_id}_${image.seed}`;
		} else if (image.type === "upload") {
			const fileNameWithoutExtension = image?.file?.name?.split(".")?.slice(0, -1)?.join(".");
			return fileNameWithoutExtension ? `${fileNameWithoutExtension}_${image.id}` : DEFAULT_EXPORT_FILE_NAME;
		} else if (image.type === "imageToImage") {
			const originalImage: PlaygroundImage = (image.config as ImageToImageConfigType)?.original_image;
			if (originalImage?.type === "upload") {
				const fileNameWithoutExtension = originalImage?.file?.name?.split(".")?.slice(0, -1)?.join(".");
				return fileNameWithoutExtension ? `${fileNameWithoutExtension}_${image.id}` : DEFAULT_EXPORT_FILE_NAME;
			}
			if (originalImage?.type === "textToImage")
				return `${(originalImage.config as TextToImageConfigType).prompt?.substring(0, 80)}_${image.id}`;
			// style_props exists only on style-transfer results; plain image-to-image (iframe edits
			// included) has none, so never interpolate them unchecked into the download name.
			const styleParts = [
				image.style_props?.face_sd_edit,
				image.style_props?.face_seed,
				image.style_props?.body_seed,
			].filter((part) => part !== undefined);
			return styleParts.length ? `${image.id}-${styleParts.join("-")}` : DEFAULT_EXPORT_FILE_NAME;
		}
		return image.type ? `${image.type}_result` : DEFAULT_EXPORT_FILE_NAME;
	};

	getAvailableImages = (): PlaygroundImage[] =>
		this.playgroundResults.flatMap((results) => results.images?.filter((image) => !image.loading && !image.error));

	getSelectedImages = (): PlaygroundImage[] =>
		this.playgroundResults.flatMap((results) =>
			results.images?.filter((image) => !image.loading && image.selected),
		);

	getSelectedImagesForExport = () => {
		const selectedImages = this.getSelectedImages();
		const availableImages = this.getAvailableImages();
		return selectedImages.length > 0 ? selectedImages : availableImages;
	};

	openSelected = () => {
		const selectedImages = this.getSelectedImages();
		if (selectedImages.length) {
			const newImages: PlaygroundImage[] = selectedImages.map((image) => ({ ...image, id: uuidv4() }));
			this.playgroundResults = [...this.playgroundResults, { id: uuidv4(), images: newImages, type: "new" }];
			this.selectImages(newImages);
		}
	};

	saveMyImages = async (action: SaveImagesLoadingState) => {
		try {
			this[action] = true;
			const selectedImages = this.getSelectedImages();
			for (const selectedImage of selectedImages) {
				const res = await this.rootStore.imagesStore.uploadImageViaAPI(
					undefined,
					this.rootStore.imagesStore.uploadToUserGallery.toString(),
					selectedImage.url,
				);
				if (res?.imageUrl) {
					this.uploadedImagesURLs.push(res?.imageUrl);
				}
			}
			this.showSuccessToastSavedImage = true;
			this.rootStore.imagesStore.uploadProcessDone = true;
			this[action] = false;
		} catch (e: any) {
			this[action] = false;
			return Promise.reject(`Error saving images : ${e.message || e.toString()}`);
		}
	};
	openModal = () => (this.isText2ImagePopupOpened = true);
	closeModal = () => {
		this.isText2ImagePopupOpened = false;
		this.setTextToGenerate("");
		this.setSelectedAspectRatio("4:3");
		this.rootStore.imagesStore.setProperty("isLoadingWizardImages", false);
		const showDesignEditorContainer =
			this.rootStore?.designEditorStore?.containerRef && this.rootStore.designEditorStore.containerRef?.current;
		if (showDesignEditorContainer) this.rootStore.uiStore.showDesignEditorContainer();
	};

	addOrUpdateFeedback = async (newFeedback: Feedback): Promise<void> => {
		try {
			this.loadingFeedback = true;
			const index = this.feedbackHistory.findIndex(
				(feedback: Feedback) => feedback.imageUrl === newFeedback.imageUrl,
			);

			const isUpdate: boolean = index !== -1;
			if (isUpdate) {
				this.feedbackHistory[index] = newFeedback;
			} else {
				this.feedbackHistory.push(newFeedback);
			}

			// Send raw feedback data to unified endpoint - server will handle data preparation
			const response = await this.feedbackService.sendFeedback(newFeedback, isUpdate);
			if (Number(localStorage.getItem(LocalStorageConstants.FEEDBACK_COUNT)) >= 3) {
				// Feedback reminder for all playgrounds
				await this.rootStore.authStore.user?.updateSettings(USER_SETTINGS.hideFeedbackReminder, true);
				await this.rootStore.authStore.user?.refreshSettings();
			}

			// Currently used just in the TGModelEvaluation page
			this.lastFeedback = {
				feedbackType: newFeedback.type as FeedbackTypeEnum,
				data: newFeedback,
				response,
			};

			runInAction(() => {
				this.loadingFeedback = false;
			});
		} catch (e) {
			runInAction(() => {
				this.loadingFeedback = false;
			});
			return Promise.reject(e);
		}
	};

	clearSelectedImages = () => {
		runInAction(() => {
			this.selectedImages = [];
			this.playgroundResults.forEach((result) =>
				result.images?.forEach((image) => {
					image.selected = false;
				}),
			);
		});
	};

	handleSelectImage = (image: PlaygroundImage) => {
		runInAction(() => {
			const existingImageIndex = this.selectedImages?.findIndex((selectedImage) => selectedImage.id === image.id);

			const resultIndex = this.playgroundResults.findIndex((result) =>
				result.images?.some((img) => img.id === image.id),
			);

			if (resultIndex !== 0) {
				if (existingImageIndex !== -1) {
					this.selectedImages.splice(existingImageIndex, 1);
				} else {
					if (this.selectedImages.length < 2) {
						this.selectedImages.push(image);
					}
				}

				this.playgroundResults.forEach((result) => {
					result.images?.forEach((img) => {
						img.selected = this.selectedImages.some((selectedImage) => selectedImage.id === img.id);
					});
				});
			}
		});
	};

	get selectedSingleImage(): PlaygroundImage | null {
		return this.playgroundResults.flatMap((results) => results.images?.filter((image) => image.selected))[0];
	}

	handleSelectSingleImage = (selectedImage: PlaygroundImage | null) => {
		this.singleSelectedImage = null;

		this.playgroundResults.forEach((result) =>
			result.images?.forEach((image) => {
				image.selected = selectedImage?.id === image.id;
				if (image.selected) this.singleSelectedImage = image;
			}),
		);
	};

	openUploadModal = () => {
		this.isUploadModalOpened = true;
	};
	closeUploadModal = () => {
		this.isUploadModalOpened = false;
	};

	removeNotLoadingImages = () => {
		const len = this.playgroundResults.length;
		if (len === 0) return;

		const lastIndex = len - 1;
		const last = this.playgroundResults[lastIndex];
		const images = Array.isArray(last?.images) ? last.images : [];
		const loaded = images.filter((img) => !img.loading && !!img.url);

		runInAction(() => {
			if (loaded.length === 0) {
				this.playgroundResults = this.playgroundResults.slice(0, lastIndex);
			} else if (loaded.length !== images.length) {
				const updated = { ...last, images: loaded };
				const next = this.playgroundResults.slice();
				next[lastIndex] = updated;
				this.playgroundResults = next;
			}
		});
	};

	private generateImageLayer = async ({
		imageUrl,
		getSegments,
		generateSegments,
		getLayers,
		mapLayers,
		layerName,
	}: any) => {
		if (!imageUrl) {
			console.error("No image selected for processing.");
			return;
		}

		runInAction(() => {
			this.isLoadingReflections = true;
		});

		try {
			const existingSegments = getSegments(imageUrl)?.segments;
			const segments = existingSegments ?? (await generateSegments(imageUrl));
			if (!segments) {
				console.error("Failed to generate segmentation masks.");
				return;
			}

			runInAction(() => {
				this.imagesSegments.push({ url: imageUrl, segments });
			});

			const existingReflections = this.getAutomotiveImageReflections(imageUrl);
			if (!existingReflections) {
				const response = await getLayers(imageUrl, segments);
				if (
					response.error ||
					(layerName === "tires" && (response.layer_url === "" || response.layer_url === undefined))
				) {
					showErrorToast(
						"Try again with a supported surface.",
						`error-${Date.now()}`,
						"Uh oh! This feature only works on snow, mud, or grass.",
					);
					throw new Error(`Failed to generate ${layerName} layers: ${response.error}`);
				} else {
					const mappedLayers = mapLayers(response, segments);

					runInAction(() => {
						this.imagesReflections.push({
							url: imageUrl,
							layers: mappedLayers,
						});
					});
				}
			}
		} catch (error) {
			console.error(`Error generating ${layerName} layers:`, error);
			throw error;
		} finally {
			runInAction(() => {
				this.isLoadingReflections = false;
			});
		}
	};

	generateReflectionLayers = async () => {
		try {
			const imageUrl = this.singleSelectedImage?.url;

			await this.generateImageLayer({
				imageUrl,
				getSegments: this.getAutomotiveImageSegments,
				generateSegments: this.automotiveQueryService.generateSegmentation,
				getLayers: this.automotiveQueryService.getReflectionLayers,
				mapLayers: (response: any) =>
					Object.entries(response.layers).map(([name, url]) => ({
						name,
						url: (url as string) || null,
						intensity: name === "body" ? DEFAULT_BODY_INTENSITY : DEFAULT_WINDOWS_INTENSITY,
						hidden: false,
					})),
				layerName: "reflection",
			});
		} catch (error) {
			console.error("Error generating reflection layers:", error);
		}
	};

	generateTiresLayers = async () => {
		try {
			const imageUrl = this.singleSelectedImage?.url;

			await this.generateImageLayer({
				imageUrl,
				getSegments: this.getAutomotiveImageSegments,
				generateSegments: this.automotiveQueryService.generateSegmentation,
				getLayers: (url: string, segments: any) =>
					this.automotiveQueryService.getTiersLayers(url, {
						tires: segments.tires,
					}),
				mapLayers: (response: any) => [
					{
						name: "tires",
						url: response.layer_url,
						intensity: DEFAULT_TIRES_INTENSITY,
						hidden: false,
					},
				],
				layerName: "tires",
			});
		} catch (error) {
			console.error("Error generating tire layers:", error);
		}
	};

	editReflectionLayer = async (mask: string, newReflection: File) => {
		try {
			const imageUrl = this.singleSelectedImage?.url;
			if (!imageUrl) {
				console.error("No image selected for processing.");
				return;
			}

			runInAction(() => {
				this.isEditingReflection = true;
			});

			// Upload the new segmentation
			const newSegment = await this.automotiveQueryService.uploadSegmentation(newReflection);
			if (!newSegment) {
				console.error("Failed to upload new segmentation.");
				return;
			}

			const imageSegmentsIndex = this.imagesSegments.findIndex((segment) => segment.url === imageUrl);
			const existingSegments = imageSegmentsIndex !== -1 ? this.imagesSegments[imageSegmentsIndex].segments : {};

			const updatedSegments = {
				...existingSegments,
				[mask]: newSegment,
			};

			runInAction(() => {
				if (imageSegmentsIndex !== -1) {
					this.imagesSegments[imageSegmentsIndex] = {
						url: imageUrl,
						segments: updatedSegments,
					};
				} else {
					this.imagesSegments.push({
						url: imageUrl,
						segments: updatedSegments,
					});
				}
			});

			const response = await this.automotiveQueryService.getReflectionLayers(imageUrl, updatedSegments);
			if (!response?.layers) {
				console.error("Failed to get updated reflection layers.");
				return;
			}

			const mappedLayers: AutomotiveReflectionLayer[] = Object.entries(response.layers).map(([name, url]) => ({
				name,
				url: (url as string) || null,
				intensity: name === "body" ? DEFAULT_BODY_INTENSITY : DEFAULT_WINDOWS_INTENSITY,
				hidden: false,
			}));

			runInAction(() => {
				const existingReflectionIndex = this.imagesReflections.findIndex(
					(reflection) => reflection.url === imageUrl,
				);

				if (existingReflectionIndex !== -1) {
					this.imagesReflections.splice(existingReflectionIndex, 1, {
						url: imageUrl,
						layers: mappedLayers,
					});
				} else {
					this.imagesReflections.push({
						url: imageUrl,
						layers: mappedLayers,
					});
				}
			});
		} catch (error) {
			console.error("Error updating reflection layer:", error);
		} finally {
			runInAction(() => {
				this.isEditingReflection = false;
			});
		}
	};

	editTireLayer = async (newTireMask: File) => {
		try {
			const imageUrl = this.singleSelectedImage?.url;
			if (!imageUrl) {
				console.error("No image selected for processing.");
				return;
			}

			runInAction(() => {
				this.isEditingReflection = true;
			});

			const newSegment = await this.automotiveQueryService.uploadSegmentation(newTireMask);
			if (!newSegment) {
				console.error("Failed to upload new tire segmentation.");
				return;
			}

			const imageSegmentsIndex = this.imagesSegments.findIndex((segment) => segment.url === imageUrl);
			const existingSegments = imageSegmentsIndex !== -1 ? this.imagesSegments[imageSegmentsIndex].segments : {};

			const updatedSegments = {
				...existingSegments,
				tires: newSegment,
			};

			runInAction(() => {
				if (imageSegmentsIndex !== -1) {
					this.imagesSegments[imageSegmentsIndex] = {
						url: imageUrl,
						segments: updatedSegments,
					};
				} else {
					this.imagesSegments.push({
						url: imageUrl,
						segments: updatedSegments,
					});
				}
			});

			const response = await this.automotiveQueryService.getTiersLayers(imageUrl, {
				tires: newSegment,
			});

			if (!response?.layer_url) {
				console.error("Failed to get updated tire layer.");
				return;
			}

			const tireLayer: AutomotiveReflectionLayer = {
				name: "tires",
				url: response.layer_url,
				intensity: 1,
				hidden: false,
			};

			runInAction(() => {
				const existingReflectionIndex = this.imagesReflections.findIndex(
					(reflection) => reflection.url === imageUrl,
				);

				if (existingReflectionIndex !== -1) {
					this.imagesReflections.splice(existingReflectionIndex, 1, {
						url: imageUrl,
						layers: [tireLayer],
					});
				} else {
					this.imagesReflections.push({
						url: imageUrl,
						layers: [tireLayer],
					});
				}
			});
		} catch (error) {
			console.error("Error updating tire layer:", error);
		} finally {
			runInAction(() => {
				this.isEditingReflection = false;
			});
		}
	};

	getAutomotiveImageReflections = (imageUrl: string): AutomotiveImageReflection | null => {
		return this.imagesReflections.find((reflection) => reflection.url === imageUrl) || null;
	};

	getAutomotiveImageEffects = (imageUrl: string): AutomotiveImageReflection | null => {
		return this.imagesEffects.find((reflection) => reflection.url === imageUrl) || null;
	};

	getAutomotiveImageHarmonizations = (imageUrl: string): AutomotiveImageReflection | null => {
		return this.imagesHarmonizations.find((reflection) => reflection.url === imageUrl) || null;
	};

	getAutomotiveImageSegments = (imageUrl: string): AutomotiveImageSegments | null => {
		return this.imagesSegments.find((segment) => segment.url === imageUrl) || null;
	};

	handleSelectReflectionLayer = (layer: AutomotiveReflectionLayer | null) => {
		runInAction(() => {
			this.selectedReflectionLayer = layer;
		});
	};

	handleSelectSegmentation = (segment: AutomotiveImageSegmentation | null) => {
		runInAction(() => {
			this.selectedSegmentation = segment;
		});
	};

	updateReflectionLayerIntensity = (imageUrl: string, layerName: string, intensity: number): void => {
		runInAction(() => {
			const imageReflectionIndex = this.imagesReflections.findIndex((reflection) => reflection.url === imageUrl);

			if (imageReflectionIndex !== -1) {
				const layers = this.imagesReflections[imageReflectionIndex].layers;

				const layerIndex = layers.findIndex((layer) => layer.name === layerName);
				if (layerIndex !== -1) {
					layers[layerIndex].intensity = intensity;
				}
			}
		});
	};

	updateEffectLayerIntensity = (imageUrl: string, layerName: string, intensity: number): void => {
		runInAction(() => {
			const imageEffectIndex = this.imagesEffects.findIndex((effect) => effect.url === imageUrl);

			if (imageEffectIndex !== -1) {
				const layers = this.imagesEffects[imageEffectIndex].layers;
				const layerIndex = layers.findIndex((layer) => layer.name === layerName);

				if (layerIndex !== -1) {
					this.imagesEffects[imageEffectIndex].layers[layerIndex].intensity = intensity;
				}
			}
		});
	};

	updateHarmonizationLayerIntensity = (imageUrl: string, layerName: string, intensity: number): void => {
		runInAction(() => {
			const imageHarmonizationIndex = this.imagesHarmonizations.findIndex((effect) => effect.url === imageUrl);

			if (imageHarmonizationIndex !== -1) {
				const layers = this.imagesHarmonizations[imageHarmonizationIndex].layers;
				const layerIndex = layers.findIndex((layer) => layer.name === layerName);

				if (layerIndex !== -1) {
					this.imagesHarmonizations[imageHarmonizationIndex].layers[layerIndex].intensity = intensity;
				}
			}
		});
	};

	showHideReflectionLayer = (imageUrl: string, layerName: string, status: boolean): void => {
		runInAction(() => {
			const imageReflectionIndex = this.imagesReflections.findIndex((reflection) => reflection.url === imageUrl);
			if (imageReflectionIndex !== -1) {
				const layers = this.imagesReflections[imageReflectionIndex].layers;
				const layerIndex = layers.findIndex((layer) => layer.name === layerName);

				if (layerIndex !== -1) {
					this.imagesReflections[imageReflectionIndex].layers[layerIndex].hidden = status;
				}
			}
		});
	};

	addLoadedImageToStore = (image: PlaygroundImage): void => {
		// Store the fully loaded image in `playGroundLoadedImages` to enable scrolling to the last result in the playground.
		this.playGroundLoadedImages = [...this.playGroundLoadedImages, image as PlaygroundImage];

		if (!iframeStore.isIframe()) {
			const user = this.rootStore.authStore.user;
			if (user && !user.settings?.[USER_SETTINGS.isUserGeneratedImage]) {
				// Update that the user has generated at least one image
				user.updateSettings(USER_SETTINGS.isUserGeneratedImage, true);
			}
		}
	};

	clearPlayGroundLoadedImagesArray = (): void => {
		// clear `playGroundLoadedImages`.
		this.playGroundLoadedImages = [];
	};

	setSelectedFiboResultId(id: string | null) {
		this.selectedFiboResultId = id;
	}

	// Lab result groups - computed view over playgroundResults
	get labResultGroups(): ILabResultGroup[] {
		return this.playgroundResults.map((r) => {
			// Check if this is a Fibo result (has Fibo config type)
			const cfg = r.config as any;
			const isFiboResult =
				cfg?.structured_prompt !== undefined ||
				cfg?.is_refine !== undefined ||
				r.type === LABS.FIBO ||
				r.type === LABS.FIBO_EDIT;

			return isFiboResult ? this.createFiboResultGroup(r) : this.createLabResultGroup(r);
		});
	}

	private createFiboResultGroup = (result: PlaygroundResult): IFiboResultGroup => {
		const cfg = result.config as any;
		const images = result.images?.filter(Boolean) ?? [];
		const hasImageLoading = images.some((i) => i?.loading);
		const hasVideoLoading = result.video?.loading || false;
		const hasLoading = hasImageLoading || hasVideoLoading;

		const firstLoaded = images.find((i) => i && !i.loading && i.url?.trim()) ?? images[0];
		const video =
			result.video ||
			(cfg?.video && typeof cfg.video === "string"
				? ({ src: cfg.video, thumbnail: "", loading: false } as PlaygroundVideoProps)
				: undefined);

		// Use selectedImageId from config if available, otherwise use first loaded or video
		const selectedImageId = cfg?.selectedImageId;
		const selectedImage = selectedImageId
			? images.find((img) => img?.id === selectedImageId) || firstLoaded || video
			: firstLoaded || video;

		return {
			id: result.id,
			loading: hasLoading || (!cfg?.structured_prompt && hasLoading),
			selectedImage: selectedImage as PlaygroundImage | PlaygroundVideoProps,
			structured_prompt: cfg?.structured_prompt,
			original_structured_prompt: cfg?.original_structured_prompt,
			structured_prompt_diff: cfg?.structured_prompt_diff,
			delta_prompt: cfg?.delta_prompt,
			seed: cfg?.seed,
			is_refine: !!cfg?.is_refine,
			is_structured_prompt_loading: !cfg?.structured_prompt && hasLoading,
			config: cfg as TextToImageConfigType | FiboEditConfigType,
		};
	};

	private createLabResultGroup = (result: PlaygroundResult): ILabResultGroup => {
		const cfg = result.config as any;
		const images = result.images?.filter(Boolean) ?? [];
		const hasImageLoading = images.some((i) => i?.loading);
		const hasVideoLoading = result.video?.loading || false;
		const hasLoading = hasImageLoading || hasVideoLoading;

		const firstLoaded = images.find((i) => i && !i.loading && i.url?.trim()) ?? images[0];
		const video =
			result.video ||
			(cfg?.video && typeof cfg.video === "string"
				? ({ src: cfg.video, thumbnail: "", loading: false } as PlaygroundVideoProps)
				: undefined);
		const selectedImage = firstLoaded || video;

		return {
			id: result.id,
			loading: hasLoading,
			selectedImage: selectedImage as PlaygroundImage | PlaygroundVideoProps,
			config: cfg as LabConfigType,
		};
	};

	isFiboResultGroup = (group?: ILabResultGroup): group is IFiboResultGroup => {
		return !!group && ("structured_prompt" in group || "is_refine" in group);
	};
}

export enum SaveImagesLoadingState {
	Save = "loadingSaveToMyImages",
	SaveAndAddToCanvas = "loadingSaveToMyImagesAndInsertImageToAd",
}
